import MuxPlayer from '@mux/mux-player-react';
import { localeToContentLang, type ContentLang } from '@yoyoclub/shared';
import { useCallback, useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { localize, toScript } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { courseApi, type PreviewVideo, type ReviewQueueItem } from '../../services/courseApi';

// Admin content review: preview each pending recorded-course video and
// approve or reject it (rejection requires a reason). All videos must be
// reviewed before a course can be purchased (PRD §4.4).
export default function CourseReview() {
  const siteLanguage = useSiteLanguage();
  const { getToken } = useAuth();
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  // previewByActivity[activityId] -> map of videoId -> preview token info
  const [previews, setPreviews] = useState<Record<string, Record<string, PreviewVideo>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // The lesson currently opened in the enlarged review modal, if any.
  const [expanded, setExpanded] = useState<{
    playbackId: string;
    token: string;
    title: string;
  } | null>(null);

  const envKey = import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined;

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const queue = await courseApi.adminListReviewQueue(token);
      setItems(queue);
      // Fetch preview tokens once per distinct activity.
      const activityIds = [...new Set(queue.map((q) => q.activity.id))];
      const entries = await Promise.all(
        activityIds.map(async (id) => {
          try {
            const vids = await courseApi.getPreviewPlayback(id, token);
            const byVideo: Record<string, PreviewVideo> = {};
            vids.forEach((v) => {
              byVideo[v.videoId] = v;
            });
            return [id, byVideo] as const;
          } catch {
            return [id, {}] as const;
          }
        })
      );
      setPreviews(Object.fromEntries(entries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the review queue');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = async (videoId: string) => {
    setBusyId(videoId);
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.adminApproveVideo(videoId, token);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (videoId: string) => {
    const reason = window.prompt('Reason for rejection (the host will see this):');
    if (!reason || !reason.trim()) return;
    setBusyId(videoId);
    try {
      const token = await getToken();
      if (!token) return;
      await courseApi.adminRejectVideo(videoId, reason.trim(), token);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout
      title="Course Video Review"
      headerRight={
        <button onClick={load} className="text-sm text-orange-600 hover:underline">
          Refresh
        </button>
      }
    >
      <div>
        {loading && <p className="text-gray-500">Loading...</p>}
        {error && <p className="mb-4 text-red-600">{error}</p>}
        {!loading && items.length === 0 && (
          <p className="text-gray-500">No videos are pending review.</p>
        )}

        <div className="space-y-6">
          {items.map((item) => {
            const preview = previews[item.activity.id]?.[item.id];
            const playable = preview?.status === 'ready' && preview.playbackId && preview.token;
            return (
              <div key={item.id} className="rounded-xl border border-gray-200 p-5">
                {/* Both languages of the course and lesson titles, machine side marked (PRD §5.2). */}
                <div className="mb-1 text-sm text-gray-500">
                  <BilingualLine
                    zh={item.activity.titleCN}
                    en={item.activity.titleEN}
                    autoLang={item.activity.titleAutoLang}
                  />
                </div>
                <div className="mb-1 flex items-center gap-3">
                  <h2 className="text-lg font-medium text-gray-900">
                    <BilingualLine
                      zh={item.titleCN}
                      en={item.titleEN}
                      autoLang={item.titleAutoLang}
                    />
                  </h2>
                  {!playable && (
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {preview?.status ?? item.status}
                    </span>
                  )}
                </div>
                <div className="mb-3 flex flex-wrap gap-x-4 text-xs text-gray-500">
                  {item.activity.host?.displayName && (
                    <span>Host: {item.activity.host.displayName}</span>
                  )}
                  <span>Submitted {fmtDate(item.createdAt)}</span>
                </div>

                {playable ? (
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded({
                        playbackId: preview.playbackId as string,
                        token: preview.token as string,
                        title: localize(item.titleCN, item.titleEN, siteLanguage),
                      })
                    }
                    className="group relative mb-4 block w-64 max-w-full overflow-hidden rounded-lg bg-black"
                    title="Click to review at full size"
                  >
                    <MuxPlayer
                      playbackId={preview.playbackId as string}
                      tokens={{ playback: preview.token as string }}
                      streamType="on-demand"
                      accentColor="#f97316"
                      {...(envKey ? { envKey } : {})}
                      style={{ width: '100%', aspectRatio: '16 / 9', pointerEvents: 'none' }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                      <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-900">
                        Click to review
                      </span>
                    </span>
                  </button>
                ) : (
                  <p className="mb-4 text-sm text-gray-500">
                    Processing video (status: {preview?.status ?? item.status}). It can be reviewed
                    once ready.
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => approve(item.id)}
                    disabled={busyId === item.id || !playable}
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    title={!playable ? 'Video must be ready before approval' : undefined}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => reject(item.id)}
                    disabled={busyId === item.id}
                    className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setExpanded(null)}
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between text-white">
              <span className="text-sm font-medium">{expanded.title}</span>
              <button
                onClick={() => setExpanded(null)}
                className="rounded px-2 py-1 text-sm hover:bg-white/10"
              >
                Close
              </button>
            </div>
            <MuxPlayer
              playbackId={expanded.playbackId}
              tokens={{ playback: expanded.token }}
              streamType="on-demand"
              accentColor="#f97316"
              autoPlay
              {...(envKey ? { envKey } : {})}
              style={{ width: '100%', aspectRatio: '16 / 9' }}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

/** "中文 (Auto-translated) / English" with whichever sides exist. */
function BilingualLine({
  zh,
  en,
  autoLang,
}: {
  zh: string | null;
  en: string | null;
  autoLang: ContentLang | null;
}) {
  const siteLanguage = useSiteLanguage();
  const zhPart = zh
    ? `${toScript(zh, siteLanguage)}${autoLang === 'ZH' ? ' (Auto-translated)' : ''}`
    : null;
  const enPart = en ? `${en}${autoLang === 'EN' ? ' (Auto-translated)' : ''}` : null;
  // The user's language first (PRD §5.2).
  const parts = (
    localeToContentLang(siteLanguage) === 'ZH' ? [zhPart, enPart] : [enPart, zhPart]
  ).filter(Boolean);
  return <>{parts.join(' / ')}</>;
}
